/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Label,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { COLORS, formatNumber } from '@marsinet/commons';
import i18next from 'i18next';
import { useToast } from '../../context/ToastProvider';
import CustomLegend from './custom-legend';
import { MarsinetContext } from '../../context/marsinetProvider';
import { formatUtcTimestamp } from '../../utils/timezone-utils';

export interface MultiLineChartProps {
  data: any[];
  title: string;
  setLoading: (loading: boolean) => void;
  chartOptions?: {
    yAxisDomain?: [number, number];
    tickCount?: number;
    tickFormatter?: (value: number) => string;
    yAxisTitle?: string;
    secondaryYAxisTitle?: string; // título del eje derecho (ej. "Ángulo (°)")

  };
  seriesLabels?: Record<string, string>; // clave de la serie -> texto en leyenda y tooltip
  highlightedPoints?: {
    timestamp: string;
    info: {
      eventType: string;
      eventId: string | null;
      params: string | null;
      value: number;
    };
  }[];

  secondaryAxisKeys?: string[]; // claves que van al eje Y derecho (ROM)
  defaultHiddenKeys?: string[]; // claves que arrancan ocultas
}

const CustomTooltip = ({
  active,
  payload,
  label,
  visibleLines,
  t,
  alarmInfo,
  getSeriesLabel,
  timeZone,
  locale,
}) => {
  if (!active && !alarmInfo) return null;

  const filteredPayload = payload.filter(
    (entry) => visibleLines[entry.dataKey ?? entry.name]
  );

  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        padding: '5px',
        maxHeight: '450px',
        maxWidth: '250px',
        fontSize: '14px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        gap: '8px',
      }}
    >
      {alarmInfo ? (
        <div>
          <p
            style={{
              fontWeight: 'bold',
              color: alarmInfo.info.eventType !== 2 ? 'red' : 'blue',
              marginBottom: '10px',
              textAlign: 'center',
            }}
          >
            {alarmInfo.info.eventType !== 2 ? 'Alarm' : 'Test'}
          </p>
          {alarmInfo.info &&
            Object.entries(alarmInfo.info).map(([key, value], index) => (
              <p key={`${key}-${index}`} style={{ color: 'black' }}>
                <span style={{ fontWeight: 600 }}>
                  {`${t(`pages.alarms.${key}`)}:`}
                </span>
                {value == null
                  ? t('pages.alarms.noParams')
                  : String(value)}{' '}
              </p>
            ))}
        </div>
      ) : (
        <>
          <p
            className="label"
            style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              textAlign: 'center',
            }}
          >
            {t(`pages.reports.graphs.timeHour`)}:{' '}
            {label
              ? formatUtcTimestamp(label, timeZone, locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })
              : ''}
          </p>
          {filteredPayload.map((entry, index) => {
            const seriesKey = entry.dataKey ?? entry.name;
            return (
              <p key={`${seriesKey}-${index}`} style={{ color: entry.stroke }}>
                {`${getSeriesLabel(seriesKey)} : `}
                <span style={{ fontWeight: 'bold' }}>
                  {formatNumber(entry.value, i18next.language)}
                </span>
              </p>
            );
          })}
        </>
      )}
    </div>
  );
};

export function MultiLineChart(
  props: Readonly<
    MultiLineChartProps & {
      legendKeyFilter?: string[];
      legendCustomLabels?: Record<string, string>;
    }
  >
) {
  const {
    data,
    title,
    setLoading,
    chartOptions = {},
    highlightedPoints = [],
    legendKeyFilter,
    legendCustomLabels,
    seriesLabels,
    secondaryAxisKeys = [],
    defaultHiddenKeys = [],
  } = props;
  const { t, i18n } = useTranslation();
  const { selectedTimezone } = useContext(MarsinetContext);
  const { showToast: show } = useToast();

  const [hoveredAlarm, setHoveredAlarm] = useState(null);

  // Texto de cada serie: primero seriesLabels, luego legendCustomLabels, si no la traducción de siempre
  const getSeriesLabel = useCallback(
    (key: string) =>
      seriesLabels?.[key] ??
      legendCustomLabels?.[key] ??
      t(`pages.maintenance.ingestion.graphs.${key}`),
    [seriesLabels, legendCustomLabels, t]
  );

  const keys = useMemo(() => {
    let allKeys =
      data.length > 0
        ? Object.keys(data[0])
          .filter((key) => key !== 'timestamp')
          .sort((a, b) => a.localeCompare(b))
          .slice(0, 24)
        : [];
    if (legendKeyFilter) {
      allKeys = allKeys.filter((key) => legendKeyFilter.includes(key));
    }
    return allKeys;
  }, [data, legendKeyFilter]);

  const [visibleLines, setVisibleLines] = useState(() =>
    Object.fromEntries(keys.map((key) => [key, !defaultHiddenKeys.includes(key)]))
  );

  const legendItems = useMemo(
    () =>
      keys.map((key, index) => ({
        key,
        color: COLORS[index % COLORS.length],
        visible: visibleLines[key],
        label: getSeriesLabel(key),
      })),
    [keys, visibleLines, getSeriesLabel]
  );

  useEffect(() => {
    setVisibleLines((prevState) => {
      const updatedState = Object.fromEntries(
        keys.map((key) => [
          key,
          prevState[key] !== undefined
            ? prevState[key]
            : !defaultHiddenKeys.includes(key),
        ])
      );
      return updatedState;
    });
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, setLoading]);

  const handleLegendClick = useCallback(
    (key: string) => {
      const visibleCount = Object.values(visibleLines).filter(Boolean).length;

      if (visibleCount >= 12 && !visibleLines[key]) {
        show(
          'warn',
          'Advertencia',
          t('pages.maintenance.ingestion.graphs.maxLegend')
        );
        return;
      }

      setVisibleLines((prevState) => ({
        ...prevState,
        [key]: !prevState[key],
      }));
    },
    [visibleLines, show, t]
  );

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500 text-lg">No data available</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div className="text-xl md:text-2xl font-bold pl-2">{title}</div>

      <ResponsiveContainer width="100%" height={430}>
        <LineChart
          data={data}
          syncId="anyId"
          margin={{ top: 20, right: 30, left: 25, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          {/* Muestra las horas UTC en la zona seleccionada. */}
          <XAxis
            dataKey="timestamp"
            tickFormatter={(timestamp) =>
              formatUtcTimestamp(timestamp, selectedTimezone, i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
              })
            }
            tick={{ fontSize: 14 }}
          />

          {/* Eje izquierdo: fuerza / umbral */}
          <YAxis
            yAxisId="left"
            domain={chartOptions?.yAxisDomain || ['auto', 'auto']}
            tickCount={chartOptions?.tickCount || 6}
            tickFormatter={
              chartOptions?.tickFormatter || ((value) => value.toString())
            }
            tick={{ fontSize: 14 }}
          >
            {chartOptions?.yAxisTitle && (
              <Label
                value={chartOptions.yAxisTitle}
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: 'middle', fontSize: 13, fill: '#374151' }}
              />
            )}
          </YAxis>

          {/* Eje derecho: ROM / ángulos, solo si hay claves asignadas */}
          {secondaryAxisKeys.length > 0 && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={['auto', 'auto']}
              tick={{ fontSize: 14 }}
            >
              {chartOptions?.secondaryYAxisTitle && (
                <Label
                  value={chartOptions.secondaryYAxisTitle}
                  angle={90}
                  position="insideRight"
                  style={{ textAnchor: 'middle', fontSize: 13, fill: '#374151' }}
                />
              )}
            </YAxis>
          )}

          {Object.values(visibleLines).some((visible) => visible) && (
            <Tooltip
              content={
                <CustomTooltip
                  active={undefined}
                  payload={undefined}
                  label={undefined}
                  visibleLines={visibleLines}
                  t={t}
                  alarmInfo={hoveredAlarm}
                  getSeriesLabel={getSeriesLabel}
                  timeZone={selectedTimezone}
                  locale={i18n.language}
                />
              }
              cursor={{
                stroke: 'black',
                strokeWidth: 2,
                strokeDasharray: '5 5',
              }}
            />
          )}

          {keys.map((key, index) => (
            <Line
              key={key}
              yAxisId={secondaryAxisKeys.includes(key) ? 'right' : 'left'}
              type="monotone"
              dataKey={key}
              stroke={COLORS[index % COLORS.length]}
              fill={COLORS[index % COLORS.length]}
              strokeOpacity={visibleLines[key] ? 1 : 0}
              // strokeWidth={2}
              strokeWidth={secondaryAxisKeys.includes(key) ? 3 : 2} // ← ROM un poco más ancha
              dot={false}
              connectNulls={true}
            />
          ))}

          {highlightedPoints.map((alarm: any, index) => (
            <g key={index}>
              <ReferenceLine
                yAxisId="left"
                x={alarm.timestamp}
                stroke={alarm.info.eventType !== 2 ? 'red' : 'blue'}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
              <ReferenceLine
                yAxisId="left"
                x={alarm.timestamp}
                stroke="transparent"
                strokeWidth={8}
                opacity={0.05}
                onMouseEnter={() => setHoveredAlarm(alarm)}
                onMouseLeave={() => setHoveredAlarm(null)}
              />
            </g>
          ))}
        </LineChart>
      </ResponsiveContainer>

      <CustomLegend items={legendItems} onToggle={handleLegendClick} />
    </div>
  );
}

export default MultiLineChart;